<script setup>
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const props = defineProps({
  chartList: {
    type: Array,
    default: () => []
  }
})
const route = useRoute()
const isFirstDay = () => {
  return `${route.params.year}/${route.params.month}/${route.params.day}` === `${chartPageStartYear}/01/01`
}
const isLastDay = () => {
  const { endYear, endMonth, endDay } = getEndYearMonthDay()
  return `${route.params.year}/${route.params.month}/${route.params.day}` === `${endYear}/${String(endMonth).padStart(2, '0')}/${String(endDay).padStart(2, '0')}`
}
const sliderValue = defineModel('sliderValue', { type: Number })
const maxIndex = computed(() => props.chartList.length - 1)
const { isPlaying, clearId, onClickPlay, onClickStop } = useAnimate(sliderValue, maxIndex)
const { rangeInput, initTouchEvents, removeTouchEvents } = customMapSlider(sliderValue, ref(0), maxIndex)
const isFirstItem = computed(() => sliderValue.value === 0)
const isLastItem = computed(() => sliderValue.value === maxIndex.value)

const selectItem = (index) => {
  if (index >= 0 && index < props.chartList.length) {
    sliderValue.value = index
  }
}
const onClickNext = () => {
  selectItem(sliderValue.value + 1)
}
const onClickPrev = () => {
  selectItem(sliderValue.value - 1)
}
const onClickNextDay = () => {
  const year = Number(route.params.year)
  const month = Number(route.params.month)
  const day = Number(route.params.day)
  if (!year || !month || !day) return
  const nextDay = dayjs(`${year}-${month}-${day}`).add(1, 'day')
  return navigateTo(`/chart/${dayjs(nextDay).format('YYYY/MM/DD')}/`)
}
const onClickPrevDay = () => {
  const year = Number(route.params.year)
  const month = Number(route.params.month)
  const day = Number(route.params.day)
  if (!year || !month || !day) return
  const prevDay = dayjs(`${year}-${month}-${day}`).subtract(1, 'day')
  return navigateTo(`/chart/${dayjs(prevDay).format('YYYY/MM/DD')}/`)
}
const onClickMove = (index) => {
  selectItem(index)
}
onMounted(() => {
  initTouchEvents()
})
onUnmounted(() => {
  clearTimeout(clearId.value)
  removeTouchEvents()
})
</script>

<template>
  <div
    class="slider__inner"
    :style="{ '--time-list-length': props.chartList.length }"
  >
    <div class="controller">
      <div class="controller__button">
        <div class="button__box">
          <button
            data-button="prevDay"
            :disabled="isFirstDay()"
            @click="onClickPrevDay"
          />
          <button
            data-button="prev"
            :disabled="isFirstItem"
            @click="onClickPrev"
          />
        </div>
        <p>
          {{ dayjs.unix(props.chartList[sliderValue].tm).tz('Asia/Tokyo').format('M/D (ddd) HH:mm') }}
        </p>
        <div class="button__box">
          <button
            data-button="next"
            :disabled="isLastItem"
            @click="onClickNext"
          />
          <button
            data-button="nextDay"
            :disabled="isLastDay()"
            @click="onClickNextDay"
          />
        </div>
      </div>
    </div>
    <div class="content">
      <div class="anime__button">
        <button
          v-show="!isPlaying"
          data-button="play"
          @click="onClickPlay"
        />
        <button
          v-show="isPlaying"
          data-button="stop"
          @click="onClickStop"
        />
      </div>
      <div class="range">
        <input
          ref="rangeInput"
          v-model.number="sliderValue"
          type="range"
          min="0"
          :max="maxIndex"
          step="1"
        >
        <div
          class="list"
        >
          <p
            v-for="(item, index) in chartList"
            :key="index"
            class="scale"
          >
            <span @click="onClickMove(index)">
              {{ dayjs.unix(item.tm).tz('Asia/Tokyo').format('H') }}時
            </span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.slider__inner {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  padding: 10px 10px 5px 5px;
  background-color: #eef1f8;
  gap: 16px;
  width: 100%;
  overflow-x: hidden;
  .toggle {
    position: absolute;
    width: 21px;
    height: 21px;
    z-index: 2;
    top: 12px;
    right: 5px;
    background-color: #fff;
    border-radius: 50%;
    box-shadow: 0 2px 2px #00000026;
    cursor: pointer;
  }
  .toggle::before,
  .toggle::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 12px;
    height: 2px;
    border-radius: 100vmax;
    background-color: #3569C0;
    content: '';
  }
  .toggle::before {
    transform: translate(-50%, -50%);
  }
  .toggle::after {
    transform: translate(-50%, -50%) rotate(90deg);
    transition: transform .2s ease-in-out;
  }
}
.controller {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 100%;
  user-select: none;
  position: relative;
  .anime__button {
    position: absolute;
    top: 0;
    left: 0;
    margin-top: -4px
  }
  .controller__button {
    display: flex;
    align-items: center;
    gap: 5px;
    user-select: none;
    p {
      min-width: 150px;
      padding: 6px 15px 5px;
      background-color: #fff;
      border-radius: 100vmax;
      color: #303030;
      font-size: 14px;
      font-weight: 600;
      line-height: 14px;
      text-align: center;
    }
    .button__box {
      display: flex;
      gap: 10px;
    }
  }
}
.content {
  display: flex;
  gap: 10px;
  width: 100%;
}
button {
  width: 25px;
  height: 25px;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 2px 0 rgb(0 0 0 / 0.15);
  background-position: center;
  background-repeat: no-repeat;
  background-size: 25px;
  cursor: pointer;
  &[data-button="prev"] {
    background-size: 20px;
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/time_prev.svg");
  }
  &[data-button="next"] {
    background-size: 20px;
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/time_next.svg");
  }
  &[data-button="prevDay"] {
    background-size: 20px;
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/day_prev.svg");
  }
  &[data-button="nextDay"] {
    background-size: 20px;
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/day_next.svg");
  }
  &[data-button="play"] {
    background-size: 12px;
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_play.svg");
  }
  &[data-button="stop"] {
    background-size: 12px;
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_pause.svg");
  }
  &:disabled {
    opacity: 0.4;
    pointer-events: none;
  }
  @media (any-hover: hover) {
    transition: opacity .2s ease-in-out;
  }
}
.range {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  flex: 1;
  position: relative;
  &::before {
    background-color: #fff;
    content: "";
    height: 8px;
    position: absolute;
    width: 100%;
  }
  input {
    position: relative;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-color: transparent;
    cursor: pointer;
    width: 100%;
    height: 8px;
    margin-inline:auto;
    outline: none;
    touch-action: pan-x;
    z-index: 3;
    -webkit-tap-highlight-color: transparent;
    &:disabled {
      opacity: 0.4;
      pointer-events: none;
    }
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      background-color: #3569C0;
      width: 10px;
      height: 22px;
      border-radius: 5px;
      box-shadow: 4px rgba(0, 0, 0, 0);
      border: 1px solid #FFF;
    }
    &::-moz-range-thumb {
      background-color: #3569C0;
      width: 10px;
      height: 22px;
      border-radius: 5px;
      box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.25);
      border: 1px solid #FFF;
    }
  }
  .list {
    display: flex;
    user-select: none;
    width: calc((100% - 10px)/(var(--time-list-length) - 1)*var(--time-list-length));
    transform: translateY(-9px);
    z-index: 2;
    pointer-events: none;
  }
  .scale {
    position: relative;
    flex: 1;
    padding-top: 15px;
    text-align: center;
    font-size: 11px;
    font-weight: 300;
    line-height: 7px;
    color: #303030;
    width: calc(100% / var(--time-list-length));
    white-space: nowrap;
    z-index: 2;
    display: flex;
    justify-content: center;
    &::before {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 1px;
      height: 16px;
      background-color: #343D53;
      content: '';
    }
    span {
      display: flex;
      justify-content: center;
      width: 100%;
      max-width: 30px;
      pointer-events: auto;
      cursor: pointer;
    }
  }
}
@media screen and (min-width: 830px) {
  .content {
    gap: 20px;
  }
  .slider__inner {
    padding: 10px 15px 5px 10px;
  }
  .range {
    input {
      &::-webkit-slider-thumb {
        width: 7px;
        border-radius: 3.5px;
      }
      &::-moz-range-thumb {
        width: 7px;
        border-radius: 3.5px;
      }
    }
    .list {
      width: calc((100% - 7px)/(var(--time-list-length) - 1)*var(--time-list-length));
    }
    .scale {
      font-size: 13px;
    }
  }
  button {
    @media (any-hover: hover) {
      &:hover {
        opacity: .6;
      }
    }
  }
}
</style>
